import { Network, FolderKanban, ListTodo, Target, Radar, ChartPie, ListChecks } from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { useEmployeeOverview } from '../widgets/employee/hooks/useEmployeeOverview';
import { usePendingTasks } from '../widgets/employee/hooks/usePendingTasks';
import { useActiveProjects } from '../widgets/employee/hooks/useActiveProjects';
import { DashboardHero } from '../infrastructure/widgets/DashboardHero';
import { DonutChart } from '../infrastructure/widgets/DonutChart';
import { KpiCard, SectionCard, SimpleList, SimpleListItem } from '../infrastructure/widgets/DashboardKit';

const ROLE_LABELS: Record<string, string> = {
  manager: 'Manager', team_lead: 'Team Lead', bde: 'BDE', developer: 'Developer',
};

const PROJECT_STATUS_COLOR: Record<string, string> = {
  active: '#12b76a', on_track: '#12b76a', at_risk: '#f79009', blocked: '#f04438', done: '#667085', completed: '#667085',
};

export function OperationalDashboard() {
  const user = useUser();
  const roleLabel = ROLE_LABELS[user?.role ?? ''] ?? 'Team';

  const { data: overview } = useEmployeeOverview();
  const { data: tasksData } = usePendingTasks();
  const { data: projectsData } = useActiveProjects();

  const tasks = tasksData?.tasks ?? [];
  const openTasks = tasks.filter((t) => t.status !== 'done');
  const overdueTasks = openTasks.filter((t) => t.due_date && new Date(t.due_date) < new Date());

  const projects = projectsData?.projects ?? [];
  const activeProjects = projects.filter((p) => p.status !== 'done' && p.status !== 'completed');

  const projectStatusCounts = projects.reduce<Record<string, number>>((acc, p) => {
    const key = p.status || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const taskItems: SimpleListItem[] = openTasks.slice(0, 6).map((t) => ({
    icon: ListTodo,
    title: t.title,
    meta: t.due_date ? `Due ${new Date(t.due_date).toLocaleDateString()}` : 'No due date',
    tag: t.due_date && new Date(t.due_date) < new Date() ? 'Overdue' : t.status.replace('_', ' '),
    tagHex: t.due_date && new Date(t.due_date) < new Date() ? '#f04438' : '#5b45d6',
  }));

  const projectItems: SimpleListItem[] = activeProjects.slice(0, 6).map((p) => ({
    icon: FolderKanban,
    title: p.name,
    meta: `Status: ${p.status.replace('_', ' ')}`,
    tag: p.status.replace('_', ' '),
    tagHex: PROJECT_STATUS_COLOR[p.status] || '#7a63de',
  }));

  return (
    <div className="mx-auto max-w-[1440px]">
      <DashboardHero
        badge="Operations · Today"
        badgeIcon={Network}
        title={`Welcome, ${user?.displayName || roleLabel}`}
        subtitle="Your projects, tasks and lead pipeline for today."
        heroStats={[
          { label: 'Active Projects', value: String(activeProjects.length), sub: `of ${projects.length} total` },
          { label: 'Open Tasks', value: String(openTasks.length), sub: `${overdueTasks.length} overdue` },
          { label: 'My Pipeline', value: String(overview?.totalLeads ?? 0), sub: `${overview?.leadsWeek ?? 0} this week` },
        ]}
      />

      <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard label="Active Projects" value={String(activeProjects.length)} icon={FolderKanban} hex="#5b45d6" />
        <KpiCard label="Open Tasks" value={String(openTasks.length)} icon={ListTodo} hex="#7a63de" />
        <KpiCard label="Overdue" value={String(overdueTasks.length)} icon={ListChecks} hex="#f04438" />
        <KpiCard label="My Leads" value={String(overview?.totalLeads ?? 0)} icon={Target} hex="#0ba5ec" />
        <KpiCard label="Scans Today" value={String(overview?.scansToday ?? 0)} icon={Radar} hex="#f79009" />
      </div>

      <div className="grid grid-cols-12 gap-4">
        {Object.keys(projectStatusCounts).length > 0 && (
          <SectionCard icon={ChartPie} title="Project Health" subtitle="Status split across active projects" span={4}>
            <DonutChart
              segments={Object.entries(projectStatusCounts).map(([status, count]) => ({
                label: status.replace('_', ' '),
                value: count,
                color: PROJECT_STATUS_COLOR[status] || '#667085',
              }))}
              centerValue={String(projects.length)}
              centerLabel="Projects"
            />
          </SectionCard>
        )}
        <SectionCard icon={FolderKanban} title="Active Projects" subtitle={`${activeProjects.length} running`} span={8}>
          <SimpleList items={projectItems} />
        </SectionCard>
        <SectionCard icon={ListTodo} title="My Tasks" subtitle={`${openTasks.length} open · ${overdueTasks.length} overdue`} span={12}>
          <SimpleList items={taskItems} />
        </SectionCard>
      </div>
    </div>
  );
}
